import React, {
    createContext, useContext, useEffect, useRef, useCallback, useState,
} from 'react';
import { useAuth } from './AuthContext';

/**
 * Global SSE data-change channel for the « Zero-F5 » experience.
 *
 * A single EventSource is kept open for the whole app lifetime (per
 * authenticated user). Consumers subscribe via `useRealtimeEvent(type, cb)`
 * and receive every event of that type published server-side into
 * `services.realtime.publish(org_id, type, payload)`.
 *
 * Reconnection is delegated to the browser's built-in EventSource
 * behaviour; on auth change the stream is torn down and re-established.
 */
const RealtimeContext = createContext({
    subscribe: () => () => {},
    connected: false,
});

export function RealtimeProvider({ children }) {
    const { user } = useAuth();
    const esRef = useRef(null);
    // Handlers keyed by event type. Using a Set per type lets multiple
    // pages listen to the same event without stepping on each other.
    const handlersRef = useRef(new Map());
    const [connected, setConnected] = useState(false);

    const dispatch = useCallback((event) => {
        const set = handlersRef.current.get(event.type);
        if (!set) return;
        // Copy the set so a handler unsubscribing during dispatch does
        // not mutate the iteration.
        for (const fn of Array.from(set)) {
            try { fn(event.payload || {}, event); } catch (e) {
                // eslint-disable-next-line no-console
                console.warn('realtime handler crashed', e);
            }
        }
    }, []);

    useEffect(() => {
        // Only run when the user is authenticated. On logout we close
        // the stream so the browser releases the HTTP/1.1 slot.
        if (!user) {
            if (esRef.current) {
                try { esRef.current.close(); } catch { /* noop */ }
                esRef.current = null;
                setConnected(false);
            }
            return;
        }
        // Avoid duplicate connections when React StrictMode fires
        // effects twice in dev.
        if (esRef.current) return;

        const base = process.env.REACT_APP_BACKEND_URL || '';
        const es = new EventSource(`${base}/api/realtime/stream`, { withCredentials: true });
        esRef.current = es;

        es.addEventListener('hello', () => setConnected(true));
        es.onmessage = (evt) => {
            try {
                const event = JSON.parse(evt.data);
                if (event && event.type) dispatch(event);
            } catch { /* ignore malformed frames */ }
        };
        es.onerror = () => {
            // Let the browser handle reconnection. We do NOT close on
            // error — that would cancel the automatic retry.
            setConnected(false);
        };

        return () => {
            try { es.close(); } catch { /* noop */ }
            if (esRef.current === es) esRef.current = null;
            setConnected(false);
        };
    }, [user, dispatch]);

    const subscribe = useCallback((type, handler) => {
        let set = handlersRef.current.get(type);
        if (!set) {
            set = new Set();
            handlersRef.current.set(type, set);
        }
        set.add(handler);
        return () => {
            const cur = handlersRef.current.get(type);
            if (cur) {
                cur.delete(handler);
                if (cur.size === 0) handlersRef.current.delete(type);
            }
        };
    }, []);

    return (
        <RealtimeContext.Provider value={{ subscribe, connected }}>
            {children}
        </RealtimeContext.Provider>
    );
}

/**
 * Subscribe a component to a realtime event type. The handler receives
 * `(payload, event)`. Unsubscription happens automatically on unmount.
 *
 * Example:
 *   useRealtimeEvent('candidate.updated', (p) => {
 *       setCandidates(cs => cs.map(c => c.id === p.id ? { ...c, ...p } : c));
 *   });
 */
export function useRealtimeEvent(type, handler) {
    const { subscribe } = useContext(RealtimeContext);
    // Keep a ref so the effect deps stay stable — otherwise a new
    // handler function on every render would resubscribe on every render.
    const handlerRef = useRef(handler);
    useEffect(() => { handlerRef.current = handler; }, [handler]);
    useEffect(() => {
        return subscribe(type, (payload, event) => handlerRef.current(payload, event));
    }, [subscribe, type]);
}

export function useRealtime() {
    return useContext(RealtimeContext);
}
